import {computed, inject, Injectable, signal} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {
  catchError,
  EMPTY,
  exhaustMap,
  from,
  Observable,
  Subject,
  tap,
} from "rxjs";
import {ApiService, Tables} from "src/app/services/api.service";
import {ProductionLine} from "src/app/shared/interfaces/productionLine";

interface ProductionLineState {
  productionLines: ProductionLine[];
  productionLineBeingEdited: ProductionLine | null;
}

@Injectable()
export class ProductionLineService {
  apiService = inject(ApiService);

  // sources
  add$ = new Subject<ProductionLine>();
  update$ = new Subject<ProductionLine>();
  delete$ = new Subject<string>();
  productionLines$: Observable<ProductionLine[]> =
    this.apiService.getProductionLinesAdmin();
  productionLineBeingEdited$ = new Subject<ProductionLine>();

  // state
  private state = signal<ProductionLineState>({
    productionLines: [],
    productionLineBeingEdited: null,
  });

  // selectors
  productionLines = computed(() => this.state().productionLines);
  productionLineBeingEdited = computed(
    () => this.state().productionLineBeingEdited
  );

  constructor() {
    // reducers
    this.productionLines$
      .pipe(takeUntilDestroyed())
      .subscribe((productionLines: ProductionLine[]) =>
        this.state.update((state) => ({
          ...state,
          productionLines,
        }))
      );

    this.add$
      .pipe(
        takeUntilDestroyed(),
        exhaustMap((productionLine) => this.addProductionLine(productionLine)),
        catchError((error) => {
          console.error("Error adding production line:", error);
          return EMPTY;
        })
      )
      .subscribe((newProductionLine) => {
        this.state.update((state) => ({
          ...state,
          productionLines: [...state.productionLines, newProductionLine],
        }));
      });

    this.update$
      .pipe(
        takeUntilDestroyed(),
        exhaustMap((productionLine) =>
          this.updateProductionLine(productionLine)
        ),
        catchError((error) => {
          console.error("Error updating production line:", error);
          return EMPTY;
        })
      )
      .subscribe((updatedProductionLine) => {
        this.state.update((state) => ({
          ...state,
          productionLines: state.productionLines.map((productionLine) =>
            productionLine.id === updatedProductionLine.id
              ? updatedProductionLine
              : productionLine
          ),
        }));
      });

    this.delete$
      .pipe(
        takeUntilDestroyed(),
        exhaustMap((id) =>
          from(this.deleteProductionLine(id)).pipe(
            tap(() => {
              this.state.update((state) => ({
                ...state,
                productionLines: state.productionLines.filter(
                  (productionLine) => productionLine.id !== id
                ),
              }));
            }),
            catchError((error) => {
              console.error("Error deleting production line:", error);
              return EMPTY;
            })
          )
        )
      )
      .subscribe();

    this.productionLineBeingEdited$
      .pipe(takeUntilDestroyed())
      .subscribe((productionLine) =>
        this.state.update((state) => ({
          ...state,
          productionLineBeingEdited: productionLine,
        }))
      );
  }

  private addProductionLine(productionLine: ProductionLine) {
    return this.apiService.addItem(Tables.ProductionLine, productionLine);
  }

  private updateProductionLine(productionLine: ProductionLine) {
    return this.apiService.updateItem<ProductionLine>(Tables.ProductionLine, productionLine);
  }

  private deleteProductionLine(id: string) {
    return this.apiService.deleteItem(Tables.ProductionLine, id);
  }
}
